import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Lightbox from 'lightbox-react';
import 'lightbox-react/style.css';
import useFlirtMedia from 'app/hooks/useFlirtMedia';
import { FileStorefrontLink } from './FileStorefrontLink';
import { IconButton } from '@mui/material';
import { PlayArrow, Pause } from '@mui/icons-material';

const SLIDESHOW_INTERVAL_MS = 5000;

const PlayPauseButton = ({ playing, onToggle }) => (
  <IconButton
    onClick={onToggle}
    sx={{ color: 'white' }}
  >
    {playing ? <Pause /> : <PlayArrow />}
  </IconButton>
);

PlayPauseButton.propTypes = {
  playing: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

export const FileMediaViewer = ({ items, open, onClose }) => {
  const {
    currentItem,
    nextItem,
    prevItem,
    onNextClick,
    onPrevClick,
    setMediaIndex,
    pauseAllAudioPlayers,
    pauseAllVideoPlayers,
    lightboxMediaDecorator,
  } = useFlirtMedia({ mediaItems: items });

  const [playing, setPlaying] = useState(false);
  const togglePlay = () => setPlaying((prev) => !prev);

  const intervalRef = useRef(null);

  useEffect(() => {
    if (open) {
      setMediaIndex(0);
    }
  }, [open]);

  useEffect(() => {
    if (!playing) return () => {};

    const id = setInterval(onNextClick, SLIDESHOW_INTERVAL_MS);
    intervalRef.current = id;

    return () => clearInterval(id);
  }, [playing, onNextClick]);

  const handleClose = () => {
    pauseAllAudioPlayers();
    pauseAllVideoPlayers();
    clearInterval(intervalRef.current);
    setPlaying(false);
    onClose();
  };

  if (!open || !currentItem) return null;

  return (
    <Lightbox
      mainSrc={lightboxMediaDecorator(currentItem)}
      nextSrc={nextItem && lightboxMediaDecorator(nextItem)}
      prevSrc={prevItem && lightboxMediaDecorator(prevItem)}
      onCloseRequest={handleClose}
      onMovePrevRequest={onPrevClick}
      onMoveNextRequest={onNextClick}
      reactModalStyle={{
        overlay: { zIndex: 9000, backgroundColor: 'rgba(0, 0, 0, 0.95)' },
      }}
      toolbarButtons={[
        <PlayPauseButton
          key="play-pause"
          playing={playing}
          onToggle={togglePlay}
        />,
        <FileStorefrontLink
          key="file-storefront-link"
          advisorLogin={currentItem?.advisorLogin}
        />,
      ]}
    />
  );
};

FileMediaViewer.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.any,
      type: PropTypes.string,
      src: PropTypes.string,
      thumb: PropTypes.node,
      publicPreviewable: PropTypes.bool,
      advisorLogin: PropTypes.string,
    }),
  ).isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
